import { useEffect, useState } from 'react';
import { router } from '@inertiajs/react';
import TeacherShell, { TeacherPageHeader } from '../../../components/layout/TeacherShell';
import { Card, Button } from '../../../components/ui';

export default function TeacherLiveSessionNew({ eligible_students = [] }) {
  const [studentId, setStudentId] = useState('');
  const [viewType, setViewType] = useState('VocabularyPractice');
  const [mode, setMode] = useState('onboarding');
  const [vocabularyListId, setVocabularyListId] = useState('');
  const [lists, setLists] = useState([]);
  const [loadingLists, setLoadingLists] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Chapter Reading: the teacher's books and the chosen book (chapter is
  // navigated live during the session, not picked at launch).
  const [books, setBooks] = useState([]);
  const [loadingBooks, setLoadingBooks] = useState(false);
  const [bookId, setBookId] = useState('');

  const isChapter = viewType === 'ChapterReader';

  const csrfToken =
    typeof document !== 'undefined'
      ? document.querySelector('meta[name="csrf-token"]')?.content
      : '';

  // Fetch the teacher's books once, when Chapter Reading is selected.
  useEffect(() => {
    if (!isChapter || books.length > 0) return;
    let cancelled = false;
    setLoadingBooks(true);
    fetch('/teacher/live_sessions/chapter_options', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then((r) => r.json())
      .then((data) => {
        if (!cancelled) setBooks(data.books || []);
      })
      .catch(() => {
        if (!cancelled) setBooks([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingBooks(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isChapter, books.length]);

  // Fetch lists whenever the chosen student or mode changes. Onboarding mode
  // returns only lists with pending-onboarding cards for that student; review
  // mode returns only lists with the student's active (onboarded) cards.
  useEffect(() => {
    if (isChapter || !studentId) {
      setLists([]);
      setVocabularyListId('');
      return;
    }
    let cancelled = false;
    setLoadingLists(true);
    const url = `/teacher/live_sessions/list_options?invitee_user_id=${encodeURIComponent(
      studentId
    )}&mode=${encodeURIComponent(mode)}`;
    fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const next = data.lists || [];
        setLists(next);
        setVocabularyListId((prev) =>
          next.some((l) => String(l.id) === String(prev)) ? prev : ''
        );
      })
      .catch(() => {
        if (!cancelled) setLists([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingLists(false);
      });
    return () => {
      cancelled = true;
    };
  }, [studentId, mode, isChapter]);

  const canSubmit =
    studentId &&
    !submitting &&
    (isChapter ? !!bookId : mode && vocabularyListId);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    const body = isChapter
      ? { view: 'ChapterReader', view_params: { book_id: bookId } }
      : { view: 'VocabularyPractice', view_params: { mode, vocabulary_list_id: vocabularyListId } };
    router.post(
      '/teacher/live_sessions',
      { invitee_user_id: studentId, ...body },
      {
        headers: { 'X-CSRF-Token': csrfToken },
        onFinish: () => setSubmitting(false),
      }
    );
  };

  const listEmptyHint =
    !studentId
      ? 'Select a student first.'
      : loadingLists
      ? 'Loading lists…'
      : mode === 'onboarding'
      ? 'No lists with new words for this student. Try Review mode or pick a different student.'
      : 'No lists with active cards for this student. Try Onboarding mode or pick a different student.';

  return (
    <TeacherShell scrollTitle="Start a Live Session">
      <TeacherPageHeader
        title="Start a Live Session"
        subtitle="Invite a student into a real-time session"
        backLink="/teacher/live_sessions"
        backLabel="Back to lobby"
      />

      <form onSubmit={handleSubmit} className="max-w-2xl">
        <Card className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
              Student
            </label>
            {eligible_students.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No eligible students in your organization yet.
              </p>
            ) : (
              <select
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
              >
                <option value="">Select a student…</option>
                {eligible_students.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.full_name}
                    {s.email ? ` (${s.email})` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
              Session type
            </label>
            <div className="grid grid-cols-2 gap-3">
              <ModeOption
                label="Vocabulary Practice"
                description="Onboard or review words together."
                selected={!isChapter}
                onClick={() => setViewType('VocabularyPractice')}
              />
              <ModeOption
                label="Chapter Reading"
                description="Read a book chapter together — you drive, the student follows."
                selected={isChapter}
                onClick={() => setViewType('ChapterReader')}
              />
            </div>
          </div>

          {!isChapter && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                Activity
              </label>
              <div className="grid grid-cols-2 gap-3">
                <ModeOption
                  label="Onboarding"
                  description="Introduce new words via the 4-stage onboarding flow."
                  selected={mode === 'onboarding'}
                  onClick={() => setMode('onboarding')}
                />
                <ModeOption
                  label="Review"
                  description="Practice words the student is already learning (FSRS review)."
                  selected={mode === 'review'}
                  onClick={() => setMode('review')}
                />
              </div>
            </div>
          )}

          {!isChapter && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
              Vocabulary list
            </label>
            {lists.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">{listEmptyHint}</p>
            ) : (
              <select
                value={vocabularyListId}
                onChange={(e) => setVocabularyListId(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
              >
                <option value="">Select a list…</option>
                {lists.map((list) => (
                  <option key={list.id} value={list.id}>
                    {list.name}
                    {list.pending_count != null
                      ? ` · ${list.pending_count} new for this student`
                      : list.active_count != null
                      ? ` · ${list.active_count} active for this student`
                      : list.word_count != null
                      ? ` · ${list.word_count} words`
                      : ''}
                    {list.list_type ? ` · ${list.list_type}` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
          )}

          {isChapter && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                Book
              </label>
              {loadingBooks ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading your library…</p>
              ) : books.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No books with parsed chapters in your library yet.
                </p>
              ) : (
                <select
                  value={bookId}
                  onChange={(e) => setBookId(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
                >
                  <option value="">Select a book…</option>
                  {books.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.title}
                      {b.author ? ` — ${b.author}` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {isChapter && bookId && (
            <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">
              You'll navigate chapters live during the session — the student follows along.
            </p>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => router.visit('/teacher/live_sessions')}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={!canSubmit}>
              {submitting ? 'Creating…' : 'Send invite'}
            </Button>
          </div>
        </Card>
      </form>
    </TeacherShell>
  );
}

function ModeOption({ label, description, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'text-left px-4 py-3 rounded-lg border transition-colors',
        selected
          ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/40 dark:border-blue-400'
          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800',
      ].join(' ')}
    >
      <div className="text-sm font-semibold text-gray-900 dark:text-white">{label}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</div>
    </button>
  );
}
